import React, { FC, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { ArchiveDashboardProps, TrainingType } from "./Types";
import { Translate } from "@/app/application/Translate";
import ArchivedSessionsDataGridDashboard from "@/features/training/pages/Dashboard/ArchivedSessionsDataGridDashboard";
import { FallBackErrorMessage } from "@/features/training/ui";
import { Box, Chip, Stack, Typography, alpha, useTheme } from "@mui/material";
import dayjs from "dayjs";

import { QuickRangeToggle } from "@/components/date-range/QuickRangeToggle";
import {
	CanonicalQuickRangeKey,
	shiftDateByQuickRange,
} from "@/components/date-range/quickRange";

export const ArchiveDashboard: FC<ArchiveDashboardProps> = ({
	initialTrainingType = TrainingType.All,
	name,
}) => {
	const theme = useTheme();
	const [trainingType] = useState<TrainingType>(initialTrainingType);
	const [dateRange, setDateRange] = useState(() => {
		const endDate = new Date();
		const startDate = shiftDateByQuickRange("3m", endDate);
		return {
			startDate,
			endDate,
			selectedRange: "3m" as CanonicalQuickRangeKey,
		};
	});

	const handleDateRangeChange = (range: CanonicalQuickRangeKey) => {
		const endDate = new Date();
		const startDate = shiftDateByQuickRange(range, endDate);
		setDateRange({ startDate, endDate, selectedRange: range });
	};

	const periodLabel = `${dayjs(dateRange.startDate).format("DD/MM/YY")} → ${dayjs(
		dateRange.endDate,
	).format("DD/MM/YY")}`;

	return (
		<Box
			sx={{ display: "flex", flexDirection: "column", minHeight: "100%" }}
		>
			{/* Header strip – styled like archive history cards */}
			<Box
				sx={(t) => ({
					py: 1.15,
					px: 1.6,
					display: "flex",
					flexWrap: "wrap",
					alignItems: "flex-start",
					justifyContent: "space-between",
					gap: 1,
					bgcolor:
						t.palette.mode === "dark"
							? alpha(t.palette.primary.main, 0.12)
							: alpha(t.palette.primary.main, 0.05),
					borderBottom: 1,
					borderColor: "divider",
					borderRadius: "14px 14px 0 0",
				})}
			>
				<Box
					sx={{ display: "flex", alignItems: "center", minWidth: 0 }}
				>
					<Box sx={{ minWidth: 0 }}>
						<Typography
							variant="overline"
							sx={{
								display: "block",
								color: "primary.main",
								fontWeight: 700,
								letterSpacing: "0.08em",
								textTransform: "uppercase",
							}}
						>
							<Translate String="Auto.Features.Training.Pages.Dashboard.ArchiveDashboard.L79C8.Archive">Archive</Translate>
						</Typography>
						<Typography
							variant="subtitle2"
							sx={{ fontWeight: 700, lineHeight: 1.15 }}
						>
							<Translate String="Training.Archive.Dashboard.Archived">
								Archived
							</Translate>{" "}
							{name}{" "}
							<Translate String="Training.Dashboard.Sessions">
								Sessions
							</Translate>
						</Typography>
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ display: "block", mt: 0.25 }}
						>
							{periodLabel}
						</Typography>
					</Box>
				</Box>

				<Stack
					direction="row"
					spacing={1}
					alignItems="center"
					useFlexGap
					flexWrap="wrap"
					sx={{ ml: "auto", justifyContent: "flex-end" }}
				>
					<Chip
						size="small"
						label={name}
						sx={{
							height: 22,
							borderRadius: 2,
							bgcolor: alpha(theme.palette.primary.main, 0.08),
							color: "primary.main",
							fontWeight: 700,
						}}
					/>
					<QuickRangeToggle
						value={dateRange.selectedRange}
						onChange={handleDateRangeChange}
						options={[
							{ value: "3m", label: "3m" },
							{ value: "6m", label: "6m" },
							{ value: "1y", label: "1y" },
						]}
						ariaLabel="Archive date range"
					/>
				</Stack>
			</Box>

			{/* Data Grid */}
			<Box
				mt={1}
				sx={{
					flex: 1,
					minHeight: 0,
					p: 1.25,
					pt: 1.1,
					borderRadius: "0 0 12px 12px",
					backgroundColor:
						theme.palette.mode === "dark"
							? alpha(theme.palette.common.white, 0.02)
							: alpha(theme.palette.primary.main, 0.015),
					borderTop: `1px solid ${alpha(theme.palette.divider, 0.78)}`,
				}}
			>
				<ErrorBoundary fallback={<FallBackErrorMessage />}>
					<ArchivedSessionsDataGridDashboard
						startDate={dateRange.startDate}
						endDate={dateRange.endDate}
						trainingType={trainingType}
					/>
				</ErrorBoundary>
			</Box>
		</Box>
	);
};
